import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { finalize, forkJoin, Observable, tap } from 'rxjs';
import { IElmServiceTaskFilterValues } from '../../interfaces/employee-lifecycle/elm-service-task-filter-values.interface';
import { IElmServiceTaskStateModel } from '../../interfaces/employee-lifecycle/elm-service-task-state-model.interface';
import { IElmServiceTask } from '../../interfaces/employee-lifecycle/elm-service-task.interface';
import { IElmServiceTaskStatusCounts } from '../../interfaces/employee-lifecycle/elm-service-task-status-counts.interface';
import { IPaginatedResponse } from '../../interfaces/IPaginatedResponse';
import { ElmServiceTaskService } from '../../services/elm-service-task.service';
import {
  AssignElmServiceTask,
  ClaimElmServiceTask,
  ClearElmServiceTask,
  ConsumeElmServiceTaskUpdate,
  LoadElmServiceTask,
  LoadElmServiceTaskBoard,
  LoadElmServiceTaskFilterValues,
  LoadElmServiceTaskStatusCounts,
  UnclaimElmServiceTask,
  UpdateElmServiceTaskMetadata,
  UpdateElmServiceTaskNote,
  UpdateElmServiceTaskStatus,
} from './elm-service-task.actions';

@State<IElmServiceTaskStateModel>({
  name: 'elmServiceTask',
  defaults: {
    boardPaginatedResponse: null,
    statusCounts: null,
    filterValues: {
      employees: [],
      assignees: [],
    },
    selectedTask: null,
    lastUpdatedTask: null,
    isBoardLoading: false,
    isStatusCountsLoading: false,
    isFilterValuesLoading: false,
    isDetailLoading: false,
    isTaskSaving: false,
  },
})
@Injectable()
export class ElmServiceTaskState {
  private readonly elmServiceTaskService = inject(ElmServiceTaskService);

  @Selector()
  public static board(state: IElmServiceTaskStateModel): IPaginatedResponse<IElmServiceTask> | null {
    return state.boardPaginatedResponse;
  }

  @Selector()
  public static statusCounts(state: IElmServiceTaskStateModel): IElmServiceTaskStatusCounts | null {
    return state.statusCounts;
  }

  @Selector()
  public static filterValues(state: IElmServiceTaskStateModel): IElmServiceTaskFilterValues {
    return state.filterValues;
  }

  @Selector()
  public static selectedTask(state: IElmServiceTaskStateModel): IElmServiceTask | null {
    return state.selectedTask;
  }

  @Selector()
  public static lastUpdatedTask(state: IElmServiceTaskStateModel): IElmServiceTask | null {
    return state.lastUpdatedTask;
  }

  @Selector()
  public static isBoardLoading(state: IElmServiceTaskStateModel): boolean {
    return state.isBoardLoading;
  }

  @Selector()
  public static isStatusCountsLoading(state: IElmServiceTaskStateModel): boolean {
    return state.isStatusCountsLoading;
  }

  @Selector()
  public static isFilterValuesLoading(state: IElmServiceTaskStateModel): boolean {
    return state.isFilterValuesLoading;
  }

  @Selector()
  public static isDetailLoading(state: IElmServiceTaskStateModel): boolean {
    return state.isDetailLoading;
  }

  @Selector()
  public static isTaskSaving(state: IElmServiceTaskStateModel): boolean {
    return state.isTaskSaving;
  }

  @Action(LoadElmServiceTaskBoard, { cancelUncompleted: true })
  public loadBoard(
    ctx: StateContext<IElmServiceTaskStateModel>,
    action: LoadElmServiceTaskBoard,
  ): Observable<IPaginatedResponse<IElmServiceTask>> {
    ctx.patchState({ isBoardLoading: true, lastUpdatedTask: null });
    return this.elmServiceTaskService.getBoard(action.paginationProps, action.assignedToMe, action.includeCompleted).pipe(
      tap(boardPaginatedResponse => ctx.patchState({ boardPaginatedResponse })),
      finalize(() => ctx.patchState({ isBoardLoading: false })),
    );
  }

  @Action(LoadElmServiceTaskStatusCounts, { cancelUncompleted: true })
  public loadStatusCounts(
    ctx: StateContext<IElmServiceTaskStateModel>,
    action: LoadElmServiceTaskStatusCounts,
  ): Observable<IElmServiceTaskStatusCounts> {
    ctx.patchState({ isStatusCountsLoading: true });
    return this.elmServiceTaskService.getBoardStatusCounts(action.paginationProps, action.assignedToMe, action.includeCompleted).pipe(
      tap(statusCounts => ctx.patchState({ statusCounts })),
      finalize(() => ctx.patchState({ isStatusCountsLoading: false })),
    );
  }

  @Action(LoadElmServiceTaskFilterValues, { cancelUncompleted: true })
  public loadFilterValues(ctx: StateContext<IElmServiceTaskStateModel>): Observable<IElmServiceTaskFilterValues> {
    ctx.patchState({ isFilterValuesLoading: true });
    return forkJoin({
      employees: this.elmServiceTaskService.getFilterEmployees(),
      assignees: this.elmServiceTaskService.getFilterAssignees(),
    }).pipe(
      tap(filterValues => ctx.patchState({ filterValues })),
      finalize(() => ctx.patchState({ isFilterValuesLoading: false })),
    );
  }

  @Action(LoadElmServiceTask)
  public loadTask(ctx: StateContext<IElmServiceTaskStateModel>, action: LoadElmServiceTask): Observable<IElmServiceTask> {
    ctx.patchState({ selectedTask: null, isDetailLoading: true });
    return this.elmServiceTaskService.getById(action.taskId).pipe(
      tap(selectedTask => ctx.patchState({ selectedTask })),
      finalize(() => ctx.patchState({ isDetailLoading: false })),
    );
  }

  @Action(ClearElmServiceTask)
  public clearTask(ctx: StateContext<IElmServiceTaskStateModel>): void {
    ctx.patchState({ selectedTask: null });
  }

  @Action(ConsumeElmServiceTaskUpdate)
  public consumeTaskUpdate(ctx: StateContext<IElmServiceTaskStateModel>): void {
    ctx.patchState({ lastUpdatedTask: null });
  }

  @Action(ClaimElmServiceTask)
  public claimTask(ctx: StateContext<IElmServiceTaskStateModel>, action: ClaimElmServiceTask): Observable<IElmServiceTask> {
    ctx.patchState({ isTaskSaving: true });
    return this.elmServiceTaskService.claim(action.taskId).pipe(
      tap(updatedTask => this.updateTask(ctx, updatedTask)),
      finalize(() => ctx.patchState({ isTaskSaving: false })),
    );
  }

  @Action(UnclaimElmServiceTask)
  public unclaimTask(ctx: StateContext<IElmServiceTaskStateModel>, action: UnclaimElmServiceTask): Observable<IElmServiceTask> {
    ctx.patchState({ isTaskSaving: true });
    return this.elmServiceTaskService.unclaim(action.taskId).pipe(
      tap(updatedTask => this.updateTask(ctx, updatedTask)),
      finalize(() => ctx.patchState({ isTaskSaving: false })),
    );
  }

  @Action(AssignElmServiceTask)
  public assignTask(ctx: StateContext<IElmServiceTaskStateModel>, action: AssignElmServiceTask): Observable<IElmServiceTask> {
    ctx.patchState({ isTaskSaving: true });
    return this.elmServiceTaskService.assign(action.taskId, action.assignedToId).pipe(
      tap(updatedTask => this.updateTask(ctx, updatedTask)),
      finalize(() => ctx.patchState({ isTaskSaving: false })),
    );
  }

  @Action(UpdateElmServiceTaskMetadata)
  public updateTaskMetadata(
    ctx: StateContext<IElmServiceTaskStateModel>,
    action: UpdateElmServiceTaskMetadata,
  ): Observable<IElmServiceTask> {
    ctx.patchState({ isTaskSaving: true });
    return this.elmServiceTaskService.updateMetadata(action.taskId, action.payload).pipe(
      tap(updatedTask => this.updateTask(ctx, updatedTask)),
      finalize(() => ctx.patchState({ isTaskSaving: false })),
    );
  }

  @Action(UpdateElmServiceTaskNote)
  public updateTaskNote(ctx: StateContext<IElmServiceTaskStateModel>, action: UpdateElmServiceTaskNote): Observable<IElmServiceTask> {
    ctx.patchState({ isTaskSaving: true });
    return this.elmServiceTaskService.updateNote(action.taskId, action.serviceWorkerNote).pipe(
      tap(updatedTask => this.updateTask(ctx, updatedTask)),
      finalize(() => ctx.patchState({ isTaskSaving: false })),
    );
  }

  @Action(UpdateElmServiceTaskStatus)
  public updateTaskStatus(ctx: StateContext<IElmServiceTaskStateModel>, action: UpdateElmServiceTaskStatus): Observable<IElmServiceTask> {
    ctx.patchState({ isTaskSaving: true });
    return this.elmServiceTaskService.updateStatus(action.taskId, action.status, action.serviceWorkerNote).pipe(
      tap(updatedTask => this.updateTask(ctx, updatedTask)),
      finalize(() => ctx.patchState({ isTaskSaving: false })),
    );
  }

  private updateTask(ctx: StateContext<IElmServiceTaskStateModel>, updatedTask: IElmServiceTask): void {
    const state = ctx.getState();
    const currentTask =
      state.boardPaginatedResponse?.items.find(task => task.id === updatedTask.id) ??
      (state.selectedTask?.id === updatedTask.id ? state.selectedTask : null);
    const mergedTask =
      updatedTask.permissions === null && currentTask ? this.preserveReadModelContext(updatedTask, currentTask) : updatedTask;

    ctx.patchState({
      boardPaginatedResponse: state.boardPaginatedResponse
        ? {
            ...state.boardPaginatedResponse,
            items: state.boardPaginatedResponse.items.map(task => (task.id === updatedTask.id ? mergedTask : task)),
          }
        : null,
      selectedTask: state.selectedTask?.id === updatedTask.id ? mergedTask : state.selectedTask,
      lastUpdatedTask: mergedTask,
    });
  }

  private preserveReadModelContext(updatedTask: IElmServiceTask, currentTask: IElmServiceTask): IElmServiceTask {
    return {
      ...updatedTask,
      permissions: currentTask.permissions,
      assignableEmployees: currentTask.assignableEmployees,
    };
  }
}
